import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const CONSENT_VERSION = "1.0.0";
const STORAGE_KEY = `cookie-consent-${CONSENT_VERSION}`;

const defaultPreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext(null);

const readStoredPreferences = () => {
  if (typeof window === "undefined") {
    return { preferences: defaultPreferences, hasStored: false };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { preferences: defaultPreferences, hasStored: false };
    }

    const parsed = JSON.parse(raw);
    if (
      !parsed ||
      parsed.version !== CONSENT_VERSION ||
      !parsed.preferences ||
      parsed.accepted !== true
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      return { preferences: defaultPreferences, hasStored: false };
    }

    return {
      preferences: {
        ...defaultPreferences,
        ...parsed.preferences,
        necessary: true,
      },
      hasStored: true,
    };
  } catch (error) {
    console.warn("[CookieConsent] Failed to parse stored preferences:", error);
    return { preferences: defaultPreferences, hasStored: false };
  }
};

export const CookieConsentProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    const stored = readStoredPreferences();
    return {
      preferences: stored.preferences,
      hasStoredPreferences: stored.hasStored,
      isBannerVisible: !stored.hasStored,
    };
  });
  const { preferences, hasStoredPreferences, isBannerVisible } = state;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const persistPreferences = useCallback((nextPreferences) => {
    const normalized = {
      ...defaultPreferences,
      ...nextPreferences,
      necessary: true,
    };

    setState((prev) => ({
      ...prev,
      preferences: normalized,
      hasStoredPreferences: true,
      isBannerVisible: false,
    }));

    if (typeof window !== "undefined") {
      try {
        const payload = {
          version: CONSENT_VERSION,
          accepted: true,
          preferences: normalized,
          updatedAt: new Date().toISOString(),
        };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (error) {
        console.warn("[CookieConsent] Failed to persist preferences:", error);
      }
    }
  }, []);

  const acceptAll = useCallback(() => {
    persistPreferences({ analytics: true, marketing: true });
  }, [persistPreferences]);

  const acceptNecessary = useCallback(() => {
    persistPreferences({ analytics: false, marketing: false });
  }, [persistPreferences]);

  const saveCustomPreferences = useCallback(
    (updatedCategories) => {
      persistPreferences({
        ...preferences,
        ...updatedCategories,
      });
    },
    [preferences, persistPreferences]
  );

  const openSettings = useCallback(() => setIsModalOpen(true), []);
  const closeSettings = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY) {
        return;
      }
      const updated = readStoredPreferences();
      setState((prev) => ({
        ...prev,
        preferences: updated.preferences,
        hasStoredPreferences: updated.hasStored,
        isBannerVisible: !updated.hasStored,
      }));
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const value = {
    preferences,
    isBannerVisible,
    isModalOpen,
    hasStoredPreferences,
    acceptAll,
    acceptNecessary,
    saveCustomPreferences,
    openSettings,
    closeSettings,
    STORAGE_KEY,
    CONSENT_VERSION,
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};
